import { Group, Modal, SectionBanner, useFormSubmissionController, useToast } from '@melio/penny';
import { useAnalytics, withAnalyticsContext } from '@melio/platform-analytics';
import { CreateVendorParams, ModelError, useVendors, Vendor } from '@melio/platform-api';
import { useMelioIntl } from '@melio/platform-i18n';
import { useEffect, useState } from 'react';

import {
  AddVendorFormFields,
  AddVendorFormWidget,
  getErrorsByType,
  getInlineApiErrorsFields,
  useSwitchToUnmanagedForm,
} from '../AddVendorForm';
import {
  AddVendorFormBannerApiErrorCode,
  AddVendorFormWidgetProps,
  ErrorTypeToErrorCodesMap,
} from '../AddVendorForm/types';

export type AddVendorModalWidgetWidgetProps = {
  managed?: AddVendorFormWidgetProps['managed'];
  defaultFormValues: Override<
    Partial<AddVendorFormFields>,
    {
      companyName: string;
    }
  >;
  isOpen: boolean;
  onDone: (vendorId: Vendor['id']) => void;
  onClose: VoidFunction;
  onSwitchToUnmanagedForm: (
    formData: Pick<AddVendorFormFields, 'companyName' | 'accountNumber' | 'postalCode'>
  ) => void;
};

export const AddVendorModalWidget = withAnalyticsContext<AddVendorModalWidgetWidgetProps>(
  ({
    setAnalyticsProperties,
    managed,
    defaultFormValues,
    isOpen,
    onDone,
    onClose,
    onSwitchToUnmanagedForm,
    ...props
  }) => {
    const { onSubmissionStateChange, submitButtonProps, handleSubmit } =
      useFormSubmissionController<AddVendorFormFields>();
    const { formatMessage } = useMelioIntl();

    const [apiErrorsCodes, setApiErrorsCodes] = useState<Pick<ErrorTypeToErrorCodesMap, 'inline' | 'banner'>>();

    const { create: createVendor, isMutating: isCreatingVendor } = useVendors({ enabled: false });
    const toast = useToast();

    const { track, trackMarketing } = useAnalytics();

    setAnalyticsProperties({
      PageName: 'add-a-vendor',
      Intent: 'add-vendor-details',
    });

    useEffect(() => {
      track('Vendor', 'View', { VendorType: managed ? 'directory' : 'local' });
    }, [track, managed]);

    const { onSwitchToUnmanaged, shouldSwitchToUnmanaged } = useSwitchToUnmanagedForm({
      onSwitch: ({ companyName, accountNumber, postalCode }) => {
        onSwitchToUnmanagedForm({
          companyName,
          accountNumber,
          postalCode,
        });
        setApiErrorsCodes(undefined);
      },
    });

    type TrackCreateVendorStatusParams = {
      vendorType: 'directory' | 'local';
      status: 'success' | 'failure';
      inlineErrorsFields?: (keyof AddVendorFormFields)[];
      bannerErrorsTypes?: AddVendorFormBannerApiErrorCode[];
    };
    const trackCreateVendorStatus = ({
      vendorType,
      status,
      inlineErrorsFields,
      bannerErrorsTypes,
    }: TrackCreateVendorStatusParams) => {
      const properties = {
        PageName: 'add-a-vendor',
        Intent: 'add-a-vendor',
        VendorType: vendorType,
        Status: status,
        InlineErrorsFields: inlineErrorsFields,
        BannerErrorsTypes: bannerErrorsTypes,
      };

      track('Vendor', 'Status', properties);
      status === 'success' && trackMarketing('bill-create_create-vendor', properties);
    };

    const handleCreateVendor = (data: CreateVendorParams) => {
      setApiErrorsCodes(undefined);

      createVendor(data)
        .then(({ id }) => {
          trackCreateVendorStatus({ vendorType: managed ? 'directory' : 'local', status: 'success' });
          toast({ type: 'success', title: formatMessage('addVendor.createVendorToast.success') });
          onDone(id);
        })
        .catch((error: ModelError) => handleCreateVendorFail(data, error));
    };

    const handleCreateVendorFail = (submittedData: CreateVendorParams, error: ModelError) => {
      const { inline, banner } = getErrorsByType(error);

      if (inline?.length || banner?.length) {
        trackCreateVendorStatus({
          vendorType: managed ? 'directory' : 'local',
          status: 'failure',
          ...(inline?.length ? { inlineErrorsFields: getInlineApiErrorsFields(inline) } : {}),
          ...(banner?.length ? { bannerErrorsTypes: banner } : {}),
        });
        setApiErrorsCodes({ inline, banner });
      } else {
        // TODO: add monitor for a case where getErrorsByType return unknow key.length https://meliorisk.atlassian.net/browse/ME-41490

        toast({
          type: 'error',
          title: formatMessage('addVendor.createVendorToast.error', {
            companyName: submittedData.name,
          }),
        });
      }

      if (shouldSwitchToUnmanaged(error)) {
        onSwitchToUnmanaged({
          companyName: submittedData.name,
          accountNumber: submittedData.accountNumber,
          postalCode: submittedData.managed?.zipCode,
        });
      }
    };

    const onSubmit = () => {
      track(`Vendor`, 'Click', {
        VendorType: managed ? 'directory' : 'local',
      });
      handleSubmit?.()();
    };

    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        data-testid="add-vendor-modal-widget"
        header={formatMessage('widgets.addVendor.title', { companyName: defaultFormValues.companyName })}
        primaryButton={{
          variant: 'primary',
          label: formatMessage('widgets.addVendor.saveDetails'),
          ...submitButtonProps,
          onClick: () => onSubmit(),
        }}
        {...props}
      >
        <Group variant="vertical" spacing="l" width="full">
          {apiErrorsCodes?.banner?.[0] && (
            <SectionBanner
              variant="critical"
              description={formatMessage(`widgets.addVendorForm.apiErrors.${apiErrorsCodes?.banner?.[0]}`)}
              data-testid={`add-vendor-details-api-error-banner-${apiErrorsCodes?.banner?.[0]}`}
            />
          )}
          <AddVendorFormWidget
            onSubmit={handleCreateVendor}
            onSubmissionStateChange={onSubmissionStateChange}
            isSaving={isCreatingVendor}
            managed={managed}
            defaultValues={defaultFormValues}
            shouldHideCompanyNameField
            inlineApiErrorCodes={apiErrorsCodes?.inline}
          />
        </Group>
      </Modal>
    );
  }
);

AddVendorModalWidget.displayName = 'AddVendorModalWidget';
